import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/api";
import { MobileNav } from "@/components/MobileNav";
import { SponsorStrip } from "@/components/SponsorStrip";
import { MediaPartnerStrip } from "@/components/MediaPartnerStrip";
import { MenuComunicati } from "@/components/MenuComunicati";
import { toast } from "sonner";

const FILTERS = [
  { key: "all", label: "Tutte" },
  { key: "gold", label: "Girone Gold" },
  { key: "silver", label: "Girone Silver" },
];

export default function GalleriaGiornata() {
  const { n } = useParams();
  const giornata = parseInt(n, 10);
  const [clubs, setClubs] = useState(null);
  const [filter, setFilter] = useState("all");
  const [photoModal, setPhotoModal] = useState(null);

  useEffect(() => {
    api
      .get("/clubs")
      .then((res) => setClubs(res.data))
      .catch(() => toast.error("Impossibile caricare la galleria"));
  }, []);

  const items = useMemo(() => {
    if (!clubs) return [];
    const out = [];
    for (const club of clubs) {
      for (const p of club.team_photos || []) {
        const team = (club.teams || []).find((t) => t.name === p.team) || {};
        const g = p.giornata || 1;
        if (g !== giornata) continue;
        out.push({
          team: p.team,
          photo: p.photo,
          club: club.name,
          league: team.league,
          girone: team.girone,
          giornata: g,
        });
      }
    }
    return out;
  }, [clubs, giornata]);

  const visible = filter === "all" ? items : items.filter((p) => p.league === filter);

  return (
    <div className="bg-[#e3edf6] text-[#0f172a] min-h-screen">
      <nav className="border-b border-[#e2e8f0] px-6 md:px-8 py-5 sticky top-0 bg-[#e3edf6]/90 backdrop-blur-md z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
            <img src="/logo-coppa.png" alt="Coppa dei Club" className="h-8 w-auto" />
            <span className="text-[#0284c7] font-mono-x text-xs">/ Hub</span>
          </Link>
          <div className="flex items-center gap-2 text-xs font-ui text-[#475569]">
            <a href="/#calendari" data-testid="nav-calendari" className="hidden sm:inline-block px-4 py-2 bg-white text-[#0f172a] hover:bg-[#38bdf8] transition-all rounded font-semibold border border-[#cbd5e1]">
              Calendari Gare
            </a>
            <Link to="/circoli" data-testid="nav-circoli" className="hidden sm:inline-block px-4 py-2 bg-white text-[#0f172a] hover:bg-[#38bdf8] transition-all rounded font-semibold border border-[#cbd5e1]">
              Circoli
            </Link>
            <Link to="/galleria" data-testid="nav-galleria" className="hidden sm:inline-block px-4 py-2 bg-white text-[#0f172a] hover:bg-[#38bdf8] transition-all rounded font-semibold border border-[#cbd5e1]">
              Galleria foto
            </Link>
            <MenuComunicati prefix={"/"} />
            <Link
              to="/admin"
              data-testid="nav-admin-login"
              className="hidden sm:inline-block px-4 py-2 bg-[#e2e8f0] text-[#0f172a] hover:bg-[#38bdf8] hover:text-[#0f172a] transition-all rounded font-semibold border border-[#cbd5e1]"
            >
              Login Admin
            </Link>
            <MobileNav active="galleria" />
          </div>
        </div>
      </nav>

      <SponsorStrip />

      <MediaPartnerStrip />

      <header className="py-16 md:py-20 px-6 md:px-8 border-b border-[#e2e8f0]">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/galleria"
            data-testid="galleria-back"
            className="inline-flex items-center gap-2 text-xs font-ui font-semibold text-[#0284c7] hover:underline mb-6"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
              <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Torna alla Galleria
          </Link>
          <span className="font-mono-x text-xs text-[#0284c7] tracking-widest uppercase block mb-2">
            Coppa dei Club 2026-27
          </span>
          <h1 className="font-display text-4xl md:text-5xl text-[#0f172a] mb-4">
            {giornata}° giornata
          </h1>
          <p className="text-base text-[#475569] max-w-2xl leading-relaxed">
            {clubs
              ? `${items.length} foto ufficiali della ${giornata}° giornata di campionato. Clicca su una foto per ingrandirla.`
              : "Caricamento delle foto..."}
          </p>
          <div className="flex flex-wrap gap-2 mt-6" data-testid="gallery-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`gallery-filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`px-4 py-2 rounded-full text-xs font-ui font-semibold border transition-all ${
                  filter === f.key
                    ? "bg-[#0284c7] text-white border-[#0284c7]"
                    : "bg-white text-[#475569] border-[#cbd5e1] hover:border-[#38bdf8] hover:text-[#0284c7]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="py-14 px-6 md:px-8">
        <div className="max-w-6xl mx-auto">
          {clubs && visible.length === 0 && (
            <p className="text-sm text-[#475569]" data-testid="gallery-empty">
              Nessuna foto disponibile per questo filtro.
            </p>
          )}
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5" data-testid="gallery-grid">
            {visible.map((p) => (
              <button
                key={`${p.club}-${p.team}`}
                data-testid={`gallery-item-${p.team}`}
                onClick={() => setPhotoModal(p)}
                className="relative w-full mb-5 break-inside-avoid rounded-xl overflow-hidden border border-[#e2e8f0] bg-white group cursor-pointer text-left"
              >
                <img
                  src={p.photo}
                  alt={`Foto squadra ${p.team}`}
                  loading="lazy"
                  className="w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0f172a]/80 to-transparent px-4 pt-10 pb-3 opacity-90 group-hover:opacity-100 transition-opacity">
                  <p className="text-white text-sm font-bold leading-tight">{p.team}</p>
                  <p className="text-[#bae6fd] text-[11px] font-ui">
                    {p.club}
                    {p.girone ? ` • ${p.league === "gold" ? "Gold" : "Silver"} ${p.girone}` : ""}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </main>

      {photoModal && (
        <div
          data-testid="team-photo-modal"
          className="fixed inset-0 z-[100] bg-[#0f172a]/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPhotoModal(null)}
        >
          <div
            className="bg-white rounded-2xl border border-[#e2e8f0] shadow-2xl max-w-3xl w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#e2e8f0]">
              <div>
                <h3 className="font-bold text-[#0f172a]">{photoModal.team}</h3>
                <p className="text-xs text-[#475569] font-ui">
                  {photoModal.club}
                  {photoModal.girone
                    ? ` • ${photoModal.league === "gold" ? "Girone Gold" : "Girone Silver"} ${photoModal.girone}`
                    : ""}
                  {` • ${photoModal.giornata}° giornata`}
                </p>
              </div>
              <button
                data-testid="team-photo-close"
                onClick={() => setPhotoModal(null)}
                className="w-8 h-8 rounded-full bg-[#e3edf6] text-[#475569] hover:bg-[#cbd5e1] hover:text-[#0f172a] transition-colors flex items-center justify-center"
                aria-label="Chiudi"
              >
                ✕
              </button>
            </div>
            <img
              src={photoModal.photo}
              alt={`Foto squadra ${photoModal.team}`}
              data-testid="team-photo-img"
              className="w-full max-h-[70vh] object-contain bg-[#e3edf6]"
            />
          </div>
        </div>
      )}

      <footer className="border-t border-[#e2e8f0] py-6 px-8 bg-[#d6e4f0] text-xs font-mono-x text-[#64748b] text-center">
        Coppa dei Club 2026-27 • Portale Ufficiale Calendari, Gironi Gold &amp; Silver • MSP Italia Umbria • Segreteria
        Generale:{" "}
        <a href="tel:3510262959" className="text-[#0284c7] hover:underline">
          3510262959
        </a>{" "}
        • Segreteria Coppa dei Club:{" "}
        <a href="mailto:coppadeiclubmspumbria@gmail.com" className="text-[#0284c7] hover:underline">
          coppadeiclubmspumbria@gmail.com
        </a>
      </footer>
    </div>
  );
}
